import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { RJSFSchema } from "@rjsf/utils";
import { commonSpacing, theme } from "../../../shared/theme/theme";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ManualLoader, Messages } from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetPropertiesMutation,
  useGetUnitDataMutation,
} from "../../../store/services/publicApi";
import { DropdownData } from "../../../components/FormElements/AddressFields/DropdownState/DropdownState.data";
import {
  useGetApplicationsMutation,
  useGetRebindLeasesQuery,
  useGetUserQuery,
  usePostApplicationMutation,
  usePostLeaseApplicationMutation,
  useRebindLeaseMutation,
  useSaveApplicationDataMutation,
} from "../../../store/services/privateApi";
import {
  getCurrentLease,
  isActiveLeaseStatus,
  isHomeLeaseSwitcherEnabled,
} from "src/helpers/helperLease";
import { useFlags } from "launchdarkly-react-client-sdk";
import { clearAuthReturnTo } from "src/shared/utilities/authRedirect";
import { singleHomeRebindOffer } from "src/pages/Settings/Leases";
import { TermsAndConditionsWidget } from "./TermsAndConditions";
import { ReviewInformation } from "./ReviewInformation";
import { PlaidIdentityVerificationWidget } from "./PlaidIdentityVerification";
import { StepNavigation } from "./StepNavigation";
import { FormInputWrapper } from "../../../components/FormElements/FormInput/FormInput.style";
import { RegisterReviewContainer } from "./Register.style";
import CustomFileWidget from "./CustomFileWidget";
import CustomCheckboxWidget from "./CustomCheckboxWidget";
import { IncomeVerificationWidget } from "./IncomeVerificationWidget";
import moment from "moment";
import Footer from "./Footer";
import {
  isBelowMinimumRentIncome,
  MINIMUM_INCOME_RENT_MULTIPLIER,
} from "./incomeRequirement";
import {
  buildMissingLeaseTermAvailabilityPath,
  getLeaseTermSearchParam,
  hasLeaseTermParam,
} from "./unitHelpers";
import { parseMoveInTooSoonError } from "../../../helpers/moveInTooSoon";
import type { MoveInTooSoonInfo } from "../../../helpers/moveInTooSoon";
import { MoveInDateCorrection } from "./MoveInDateCorrection";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function petFeeAmount(value: unknown): string {
  const amount = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(amount) ? String(amount) : "";
}

// Custom Rentable Items Widget
const RentableItemsWidget = (props: any) => {
  const { value, onChange, options, readonly } = props;
  const { t } = useTranslation();
  const rentableOptions = options?.rentableTypeOptions || [];

  // Initialize selected items from value
  const [selectedItems, setSelectedItems] = useState<string[]>(() => {
    // Ensure value is always treated as an array
    const valueArray = Array.isArray(value)
      ? value
      : typeof value === "string" && value
      ? [value]
      : [];

    if (valueArray.length > 0) {
      // Value is now an array of names/titles, need to map back to IDs for selection state
      return valueArray
        .map((itemName: string) => {
          const option = rentableOptions.find(
            (opt: any) => opt.title === itemName
          );
          return option?.const;
        })
        .filter(Boolean);
    }
    return [];
  });

  const handleToggle = (rentableTypeId: string) => {
    if (readonly) return;

    const isCurrentlySelected = selectedItems.includes(rentableTypeId);
    const newSelected = isCurrentlySelected
      ? selectedItems.filter((id) => id !== rentableTypeId)
      : [...selectedItems, rentableTypeId];

    setSelectedItems(newSelected);

    // Convert to the format expected by the form - just save the name
    const newValue = newSelected.map((typeId) => {
      const option = rentableOptions.find((opt: any) => opt.const === typeId);
      return option?.title || "";
    });

    onChange(newValue);
  };

  // Sync selectedItems when value changes from outside (e.g., form reset or external update)
  React.useEffect(() => {
    const valueArray = Array.isArray(value)
      ? value
      : typeof value === "string" && value
      ? [value]
      : [];

    if (valueArray.length > 0) {
      const newSelectedItems = valueArray
        .map((itemName: string) => {
          const option = rentableOptions.find(
            (opt: any) => opt.title === itemName
          );
          return option?.const;
        })
        .filter(Boolean);
      setSelectedItems(newSelectedItems);
    }
  }, [value, rentableOptions]);

  const formatAmount = (amount: any) => {
    const num = typeof amount === "number" ? amount : parseFloat(amount);
    if (!Number.isFinite(num)) return "";
    return `$${num.toFixed(2)}/mo`;
  };

  if (rentableOptions.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">
          {t(
            "account.leaseApplication.noRentableItemsAvailable",
            "No rentable items available"
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
        },
        gap: 3,
        mb: 2,
      }}
    >
      {rentableOptions.map((option: any) => {
        const isSelected = selectedItems.includes(option.const);
        return (
          <Box
            key={option.const}
            onClick={() => handleToggle(option.const)}
            sx={{
              position: "relative",
              aspectRatio: "1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 3,
              border: "3px solid",
              borderColor: isSelected ? "primary.main" : "grey.300",
              borderRadius: 3,
              cursor: readonly ? "default" : "pointer",
              backgroundColor: isSelected
                ? "rgba(25, 118, 210, 0.08)"
                : "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": readonly
                ? {}
                : {
                    borderColor: isSelected ? "primary.dark" : "primary.main",
                    boxShadow: isSelected
                      ? "0 8px 24px rgba(25, 118, 210, 0.25)"
                      : "0 4px 16px rgba(0,0,0,0.12)",
                    transform: "translateY(-4px)",
                  },
            }}
          >
            {/* Checkmark indicator */}
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 28,
                height: 28,
                border: "2px solid",
                borderColor: isSelected ? "primary.main" : "grey.400",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isSelected ? "primary.main" : "white",
                transition: "all 0.2s",
              }}
            >
              {isSelected && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3332 4L5.99984 11.3333L2.6665 8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </Box>

            {/* Icon placeholder - you can customize this */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: isSelected ? "primary.main" : "grey.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s",
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 16V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H19C20.1046 18 21 17.1046 21 16Z"
                  stroke={isSelected ? "white" : "#757575"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 10H21"
                  stroke={isSelected ? "white" : "#757575"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>

            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? "primary.main" : "text.secondary",
                textAlign: "center",
                transition: "all 0.3s",
                fontSize: "0.95rem",
                lineHeight: 1.3,
              }}
            >
              {option.title}
            </Typography>

            {/* Price */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? "primary.main" : "text.secondary",
                fontSize: "1rem",
                transition: "all 0.3s",
              }}
            >
              {formatAmount(option.amount)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Masking utilities
const removeSpaceAndSymbols = (val: any) => {
  return val?.replace(/\D/g, "");
};

const phoneNumberMask = (val: any) => {
  let num = "";
  if (val) {
    val = removeSpaceAndSymbols(val);
    if (val.length < 3) {
      num = `(${val.substring(0, 3)}`;
    } else if (val.length < 6) {
      num = `(${val.substring(0, 3)}) ${val.substring(3, 6)}`;
    } else {
      num = `(${val.substring(0, 3)}) ${val.substring(3, 6)}-${val.substring(
        6,
        10
      )}`;
    }
  }
  return (num = num.trim());
};

const ssnMask = (val: any) => {
  let num = "";
  if (val) {
    val = removeSpaceAndSymbols(val);
    if (val.length <= 3) {
      num = val;
    } else if (val.length <= 5) {
      num = `${val.substring(0, 3)}-${val.substring(3, 5)}`;
    } else {
      num = `${val.substring(0, 3)}-${val.substring(3, 5)}-${val.substring(
        5,
        9
      )}`;
    }
  }
  return (num = num.trim());
};

// Custom Phone Widget
const PhoneWidget = (props: any) => {
  const { value, onChange, id, required, readonly, label } = props;
  const [preValue, setPreValue] = useState(value || "");

  const handlePhoneChange = (event: any) => {
    let val = event.target.value;
    if (val.length > preValue.length) {
      val = phoneNumberMask(val);
    }
    setPreValue(val);
    onChange(removeSpaceAndSymbols(val));
  };

  return (
    <TextField
      id={id}
      label={label || "Phone"}
      type="tel"
      value={value ? phoneNumberMask(value) : ""}
      onChange={handlePhoneChange}
      placeholder="(123) 456-7890"
      disabled={readonly}
      required={required}
      fullWidth
      variant="outlined"
      inputProps={{
        inputMode: "numeric",
      }}
    />
  );
};

const ReadOnlyFieldTemplate = (props: any) => {
  const { classNames, schema, formData } = props;
  // You can add your custom rendering logic here
  return (
    <div
      className={classNames}
      style={{ display: "flex", flexDirection: "row" }}
    >
      <p>{schema["title"] ? schema["title"] + ": " : ""}</p>
      <p>{formData}</p> {/* Display the data */}
      {schema["description"] && <p>{schema["description"]}</p>}
    </div>
  );
};

// Custom SSN Widget
const SSNWidget = (props: any) => {
  const { value, onChange, id, required, readonly, label } = props;
  const [preValue, setPreValue] = useState(value || "");

  const handleSSNChange = (event: any) => {
    let val = event.target.value;
    if (val.length > preValue.length) {
      val = ssnMask(val);
    }
    setPreValue(val);
    onChange(val); // Pass the formatted value with hyphens to match schema pattern
  };

  return (
    <TextField
      id={id}
      label={label || "Social Security Number"}
      type="text"
      value={value ? ssnMask(value) : ""}
      onChange={handleSSNChange}
      placeholder="XXX-XX-XXXX"
      disabled={readonly}
      required={required}
      fullWidth
      variant="outlined"
      inputProps={{
        inputMode: "numeric",
      }}
    />
  );
};

// Custom Object Field Template to add Material UI icons to section titles
const CustomObjectFieldTemplate = (props: any) => {
  const { title, description, properties, idSchema } = props;

  // Suppress duplicate titles for array item objects (pets/vehicles/guarantors)
  const isArrayItem =
    typeof idSchema?.$id === "string" &&
    (idSchema.$id.includes("root_pets_") ||
      idSchema.$id.includes("root_vehicles_") ||
      idSchema.$id.includes("root_rentableItems_") ||
      idSchema.$id.includes("root_guarantors_"));

  const renderTitleWithIcon = (title: string) => {
    if (isArrayItem) return null;
    // Check for both English and translated versions

    if (title) {
      return (
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "primary.main",
            fontWeight: "medium",
          }}
        >
          {title}
        </Typography>
      );
    }
    return null;
  };

  return (
    <Box sx={{ mb: 2 }}>
      {renderTitleWithIcon(title)}
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, mt: 1 }}
          dangerouslySetInnerHTML={{ __html: description }}
        ></Typography>
      )}
      <Box sx={{ "& > *": { mb: 4 } }}>
        {properties.map((element: any, index: number) => (
          <Box key={index}>{element.content}</Box>
        ))}
      </Box>
    </Box>
  );
};

// Custom Array Field Template for better UI
const CustomArrayFieldTemplate = (props: any) => {
  const { title, items, canAdd, onAddClick, readonly, disabled } = props;
  const { t } = useTranslation();

  const getAddButtonText = (title: string) => {
    if (title?.toLowerCase().includes("pet"))
      return t("account.leaseApplication.addPet");
    if (title?.toLowerCase().includes("vehicle"))
      return t("account.leaseApplication.addVehicle");
    if (title?.toLowerCase().includes("member"))
      return t("account.leaseApplication.addMember");
    if (title?.toLowerCase().includes("rentable"))
      return t("account.leaseApplication.addRentableItem");
    if (title?.toLowerCase().includes("guarantor"))
      return t("account.leaseApplication.addGuarantor");
    if (title?.toLowerCase().includes("income"))
      return t("account.leaseApplication.addIncomeSource");
    return t("account.leaseApplication.addItem");
  };

  const getItemName = (title: string) => {
    if (title?.toLowerCase().includes("pet"))
      return t("account.leaseApplication.pet");
    if (title?.toLowerCase().includes("vehicle"))
      return t("account.leaseApplication.vehicle");
    if (title?.toLowerCase().includes("vehículo"))
      return t("account.leaseApplication.vehicle");
    if (title?.toLowerCase().includes("rentable"))
      return t("account.leaseApplication.rentableItem");
    if (title?.toLowerCase().includes("member"))
      return t("account.leaseApplication.member");
    if (title?.toLowerCase().includes("guarantor"))
      return t("account.leaseApplication.guarantor");
    if (title?.toLowerCase().includes("income"))
      return t("account.leaseApplication.incomeSource");
    return t("account.leaseApplication.item");
  };

  const getEmptyStateText = (title: string) => {
    if (title?.toLowerCase().includes("pet"))
      return t("account.leaseApplication.noPetsAdded");
    if (title?.toLowerCase().includes("rentable"))
      return t("account.leaseApplication.noRentableItemsAdded");
    if (title?.toLowerCase().includes("vehicle"))
      return t("account.leaseApplication.noVehiclesAdded");
    if (title?.toLowerCase().includes("member"))
      return t("account.leaseApplication.noMembersAdded");
    if (title?.toLowerCase().includes("guarantor"))
      return t("account.leaseApplication.noGuarantorsAdded");
    if (title?.toLowerCase().includes("income"))
      return t("account.leaseApplication.noIncomeSourcesAdded");
    return t("account.leaseApplication.noItemsAdded");
  };

  // Only show title for arrays that don't have their title handled by CustomObjectFieldTemplate
  const shouldShowTitle = (title: string) => {
    if (!title) return false;
    // These titles are already handled by CustomObjectFieldTemplate
    const skipTitles = [
      "Add Pets",
      "Add Vehicle",
      "Add Members",
      "Add Guarantors",
    ];
    return !skipTitles.some((skipTitle) => title.includes(skipTitle));
  };

  return (
    <Box sx={{ mb: 3 }}>
      {shouldShowTitle(title) && (
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "primary.main",
            fontWeight: "medium",
          }}
        >
          {title}
        </Typography>
      )}

      {canAdd && items.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            disabled={readonly || disabled}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.50",
              },
            }}
          >
            {getAddButtonText(title)}
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((element: any, index: number) => (
          <Box
            key={element.key}
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              p: 3,
              backgroundColor: "white",
              position: "relative",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                color="primary"
              >
                {getItemName(title)} #{index + 1}
              </Typography>
              {element.hasRemove && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  startIcon={<RemoveIcon />}
                  onClick={element.onDropIndexClick(element.index)}
                  disabled={readonly || disabled}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  {t("account.leaseApplication.remove")}
                </Button>
              )}
            </Box>
            <Box>{element.children}</Box>
          </Box>
        ))}
      </Box>

      {items.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            border: "2px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {getEmptyStateText(title)}
          </Typography>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddClick}
              disabled={readonly || disabled}
              sx={{ mt: 2 }}
            >
              {getAddButtonText(title)}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export const LeaseApplication = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { tpHomeLeaseSwitcher } = useFlags();
  const leaseSwitcherOn = isHomeLeaseSwitcherEnabled(tpHomeLeaseSwitcher);
  const [beds, setBeds] = useState(1);
  const [pets, setPets] = useState(2);
  const moveInMinDate = moment().subtract(60, "years").format("YYYY-MM-DD");
  const moveInMaxDate = moment().format("YYYY-MM-DD");

  // Helper to get translated schema
  const getTranslatedSchema = React.useCallback(
    (schema: RJSFSchema): RJSFSchema => {
      return JSON.parse(JSON.stringify(schema), (key, value) => {
        if (key === "title" && typeof value === "string") {
          // Map common titles to translations
          const titleMap: { [key: string]: string } = {
            // Common fields
            Phone: t("account.leaseApplication.phone"),
            State: t("account.leaseApplication.state"),
            Country: t("account.leaseApplication.country"),
            "Street Address": t("account.leaseApplication.street"),
            City: t("account.leaseApplication.city"),
            Zip: t("account.leaseApplication.zip"),
            Name: t("account.leaseApplication.name"),
            Email: t("account.leaseApplication.email"),
            Relationship: t("account.leaseApplication.relationship"),
            // Applicant Info
            "Applicant Information": t(
              "account.leaseApplication.applicantInfo"
            ),
            Birthdate: t("account.leaseApplication.birthdate"),
            "Birth Date": t("account.leaseApplication.birthDate"),
            Identification: t("account.leaseApplication.identification"),
            "Identification Number": t(
              "account.leaseApplication.identificationNumber"
            ),
            Type: t("account.leaseApplication.identificationType"),
            "Social Security Number": t("account.leaseApplication.ssnOptional"),
            "I don't have SSN": t("account.leaseApplication.noSsn"),
            "US Citizen": t("account.leaseApplication.usCitizen"),
            "Emergency Contact": t("account.leaseApplication.emergencyContact"),
            "Contact Name": t("account.leaseApplication.contactName"),
            "Upload Front Picture": t(
              "account.leaseApplication.uploadFrontPicture"
            ),
            "Upload Back Picture": t(
              "account.leaseApplication.uploadBackPicture"
            ),
            "Identity Verification Process": t(
              "account.leaseApplication.idVerificationProcess"
            ),
            // ID Types
            "Drivers License": t("account.leaseApplication.driversLicense"),
            Passport: t("account.leaseApplication.passport"),
            "ID Card": t("account.leaseApplication.idCard"),
            "Temporary Visa": t("account.leaseApplication.temporaryVisa"),
            "Residency Permit": t("account.leaseApplication.residencyPermit"),
            // Address
            "Current Address": t(
              "account.leaseApplication.currentAddressTitle"
            ),
            "Move In Date": t("account.leaseApplication.moveInDate"),
            "Reason for Leaving": t(
              "account.leaseApplication.reasonForLeaving"
            ),
            "Are you Renting?": t("account.leaseApplication.areYouRenting"),
            "Community or Landlord Name": t(
              "account.leaseApplication.landlordName"
            ),
            // Employment
            "Not Applicable": t("account.leaseApplication.notApplicable"),
            Industry: t("account.leaseApplication.industry"),
            Employer: t("account.leaseApplication.employer"),
            Title: t("account.leaseApplication.title"),
            Start: t("account.leaseApplication.startDate"),
            End: t("account.leaseApplication.endDate"),
            "Monthly Income": t("account.leaseApplication.monthlyIncome"),
            "Supervisor Name": t("account.leaseApplication.supervisorName"),
            "Email Address": t("account.leaseApplication.emailAddress"),
            "Additional Income Sources": t(
              "account.leaseApplication.otherIncomeSources"
            ),
            "Effective Date": t("account.leaseApplication.effectiveDate"),
            // Pets & Vehicles
            Pet: t("account.leaseApplication.pet"),
            Pets: t("account.leaseApplication.pets"),
            Breed: t("account.leaseApplication.breed"),
            Color: t("account.leaseApplication.color"),
            "Age (years)": t("account.leaseApplication.age"),
            "Weight (lbs.)": t("account.leaseApplication.weight"),
            Vehicle: t("account.leaseApplication.vehicle"),
            Vehicles: t("account.leaseApplication.vehicles"),
            Year: t("account.leaseApplication.year"),
            Make: t("account.leaseApplication.make"),
            Model: t("account.leaseApplication.model"),
            "License plate number": t("account.leaseApplication.licensePlate"),
            "Add Vehicle": t("account.leaseApplication.addVehicle"),
            "Do you have any vehicles to register?": t(
              "account.leaseApplication.hasVehiclesQuestion"
            ),
            "Anything else you'd like to add?": t(
              "account.leaseApplication.anythingElseToAdd"
            ),
            // Members & Guarantors
            "Will anyone else be living here": t(
              "account.leaseApplication.willAnyoneElseLive"
            ),
            "Do you want to add any guarantors to support your application?": t(
              "account.leaseApplication.addGuarantorsQuestion"
            ),
            "Add Guarantor": t("account.leaseApplication.addGuarantor"),
            Guarantor: t("account.leaseApplication.guarantor"),
            // Terms & Review
            "Terms and Conditions": t(
              "account.leaseApplication.termsConditionsTitle"
            ),
            "I agree to the terms and conditions": t(
              "account.leaseApplication.agreeToTerms"
            ),
            "Review Information": t("account.leaseApplication.reviewTitle"),
            "I confirm the information provided is correct to my knowledge": t(
              "account.leaseApplication.confirmInformation"
            ),
          };
          return titleMap[value] || value;
        }
        if (key === "enum" && Array.isArray(value)) {
          // Translate enum values
          return value.map((v: string) => {
            const enumMap: { [key: string]: string } = {
              // Dog: t("account.leaseApplication.dog"),
              // Cat: t("account.leaseApplication.cat"),
              // Bird: t("account.leaseApplication.bird"),
              // Fish: t("account.leaseApplication.fish"),
              // Other: t("account.leaseApplication.other"),
              // "Spouse/Cohabitant": t("account.leaseApplication.spouse"),
              // "Family Member - Adult": t(
              //   "account.leaseApplication.familyAdult"
              // ),
              // "Family Member - Minor": t(
              //   "account.leaseApplication.familyMinor"
              // ),
              // Roommate: t("account.leaseApplication.roommate"),
            };
            return enumMap[v] || v;
          });
        }
        return value;
      });
    },
    [t]
  );

  const globalSchema: RJSFSchema = {
    definitions: {
      phone: {
        type: "string",
        title: "Phone",
        format: "string",
        minLength: 10,
        maxLength: 10,
        pattern: "^[1-9]\\d{1,10}$",
      },
      state: {
        type: "string",
        title: "State",
        enum: DropdownData.map((d) => d.abbreviation),
      },
      address: {
        type: "object",
        title: "",
        properties: {
          country: { type: "string", title: "Country", default: "USA" },
          street: { type: "string", title: "Street Address" },
          city: { type: "string", title: "City" },
          state: {
            type: "string",
            title: "State",
            enum: DropdownData.map((d) => d.abbreviation),
          },
          zip: { type: "string", title: "Zip" },
        },
      },
      requiredAddress: {
        type: "object",
        title: "",
        properties: {
          country: { type: "string", title: "Country", default: "USA" },
          street: { type: "string", title: "Street Address" },
          city: { type: "string", title: "City" },
          state: {
            type: "string",
            title: "State",
            enum: DropdownData.map((d) => d.abbreviation),
          },
          zip: { type: "string", title: "Zip" },
        },
        required: ["street", "city", "state", "zip"],
      },
    },
  };

  const [searchParams] = useSearchParams();
  const [propertyID, setPropertyID] = useState<string | null>(
    searchParams.get("propertyID") ?? (searchParams.get("propertyId") as string)
  );
  const [unitID, setUnitID] = useState<string | null>(
    searchParams.get("unitID")
  );
  const [moveInDate, setMoveInDate] = useState<string | null>(
    searchParams.get("moveInDate")
  );
  const [term, setTerm] = useState<string | null>(
    getLeaseTermSearchParam(searchParams)
  );
  const [unitNumber, setUnitNumber] = useState<string | null>(
    searchParams.get("unitNumber")
  );
  const [leaseId, setLeaseId] = useState<string | null>(
    searchParams.get("leaseId")
  );
  const [personId, setPersonId] = useState<string | null>(
    searchParams.get("personId")
  );

  const [role, setRole] = useState<string | null>("Head of Household");

  const [applicationId, setApplicationId] = useState<any>(
    searchParams.get("applicationId")
  );

  const [propertyDetails, setPropertyDetails] = useState({
    id: "",
    name: "",
    city: "",
    abbreviation: "",
    isIdentityVerificationEnabled: false,
    isProofupVerificationEnabled: false,
  });

  const [idVerificationByPassed, setIdVerificationByPassed] = useState(false);

  const [unitData, setUnitData] = useState<any>(null);

  const rentableTypeOptions = React.useMemo(() => {
    if (!Array.isArray(unitData?.rentableItems)) return [];

    return unitData.rentableItems
      .map((item: any, idx: number) => {
        const value = item?.rentableItemTypeId;
        const label = item?.name;
        const typeAmount = item?.amount;
        if (!value) return null;

        const childOptions = Array.isArray(item?.rentableItems)
          ? item.rentableItems
              .map((ri: any, riIdx: number) => {
                const childValue = ri?.rentableItemId;
                const childLabel = ri?.name;
                if (!childValue) return null;
                return { const: childValue, title: childLabel };
              })
              .filter(Boolean)
          : [];

        if (childOptions.length === 0) return null;

        return {
          const: value,
          title: label,
          amount: typeAmount,
        };
      })
      .filter(Boolean);
  }, [unitData]);

  const [
    getUnitDataMutation,
    {
      isLoading: isLoadingGetUnit,
      error: getUnitError,
      isError: isGetUnitError,
    },
  ] = useGetUnitDataMutation();

  const schema1: RJSFSchema = {
    stepTitle: t("account.leaseApplication.applicantInfo"),
    type: "object",
    definitions: {
      identification: {
        type: "object",
        properties: {
          number: { type: "string", title: "Identification Number" },
          type: {
            type: "string",
            title: "Type",
            oneOf: [
              { title: "Drivers License", enum: ["drivers_license"] },
              { title: "Passport", enum: ["passport"] },
              { title: "ID Card", enum: ["id_card"] },
              {
                title: "Residency Permit",
                enum: ["residence_permit_card"],
              },
              { title: "Temporary Visa", enum: ["visa"] },
            ],
          },
          ssn: {
            type: "string",
            title: "Social Security Number",
            pattern: "^\\d{3}-\\d{2}-\\d{4}$",
          },
          noSSN: {
            type: "boolean",
            title: "I don't have SSN",
            format: "checkbox",
            default: false,
          },
          // usCitizen: {
          //   type: "boolean",
          //   title: "US Citizen",
          //   format: "checkbox",
          //   default: false,
          // },
        },
        required: ["number", "type"],
      },
      emergencyContact: {
        type: "object",
        title: "Emergency Contact",
        properties: {
          name: { type: "string", title: "Name" },
          relationship: { type: "string", title: "Relationship" },
          phone: globalSchema.definitions!.phone,
        },
        required: ["name", "relationship", "phone"],
      },
      applicantInfo: {
        title: "Applicant Information",
        properties: {
          name: { type: "string", title: "Name" },
          birthdate: { type: "string", title: "Birthdate", format: "date" },
          phone: globalSchema.definitions!.phone,
          // email: { type: "string", title: "Email", format: "email" },
          identification: {
            $ref: "#/definitions/identification",
            title: "",
          },
          emergencyContact: {
            $ref: "#/definitions/emergencyContact",
          },
        },
        required: [
          "name",
          "birthdate",
          "phone",
          // "email",
          "identification",
          "emergencyContact",
        ],
      },
    },
    properties: {
      applicantInfo: {
        $ref: "#/definitions/applicantInfo",
      },
    },
  };
  const schema2: RJSFSchema = {
    stepTitle: t("account.leaseApplication.idVerification"),
    type: "object",
    title: "",
    properties: {
      plaidIdentityVerification: {
        type: "string",
        title: "Identity Verification Process",
      },
    },
  };
  const schema3: RJSFSchema = {
    stepTitle: t("account.leaseApplication.currentAddress"),
    definitions: {
      currentAddress: {
        type: "object",
        title: "Current Address",
        properties: {
          address: globalSchema.definitions!.requiredAddress,
          moveInDate: { type: "string", title: "Move In Date", format: "date" },
          reasonForLeaving: { type: "string", title: "Reason for Leaving" },
          rented: {
            type: "boolean",
            title: "Are you Renting?",
            format: "checkbox",
            default: false,
          },
        },
        dependencies: {
          rented: {
            oneOf: [
              {
                properties: {
                  rented: {
                    enum: [false],
                  },
                },
              },
              {
                properties: {
                  rented: {
                    enum: [true],
                  },
                  landlord: {
                    type: "string",
                    title: "Community or Landlord Name",
                  },
                  landlordContact: { type: "string", title: "Contact Name" },
                  landlordPhone: globalSchema.definitions!.phone,
                },
                required: ["landlord", "landlordContact", "landlordPhone"],
              },
            ],
          },
        },
        required: ["moveInDate"],
      },
    },
    type: "object",
    properties: {
      currentAddress: {
        $ref: "#/definitions/currentAddress",
        title: "Current Address",
      },
    },
  };
  const schema4: RJSFSchema = {
    stepTitle: t("account.leaseApplication.employment"),
    title: t("account.leaseApplication.employmentTitle"),
    definitions: {
      employment: {
        type: "object",
        title: "",
        properties: {
          industry: {
            type: "string",
            title: "Industry",
            enum: [
              "Automotive",
              "Construction",
              "Education",
              "Finance & Insurance",
              "Government",
              "HealthCare",
              "Hospitality",
              "Manufacturing",
              "Other",
              "Public Admin",
              "Real Estate",
              "Retail",
              "Service",
              "Technology",
              "Unknown",
            ],
          },
          employer: { type: "string", title: "Employer" },
          title: { type: "string", title: "Title" },
          dateFrom: { type: "string", title: "Start", format: "date" },
          currentlyEmployed: {
            type: "boolean",
            title: t(
              "account.leaseApplication.currentlyEmployed",
              "I currently work here"
            ),
            format: "checkbox",
            default: true,
          },
          dateTo: { type: "string", title: "End", format: "date" },
          superVisorName: { type: "string", title: "Supervisor Name" },
          superVisorPhone: globalSchema.definitions!.phone,
          email: {
            type: "string",
            title: "Email Address",
            format: "email",
          },
          address: globalSchema.definitions!.requiredAddress,
        },
        required: ["employer", "dateFrom", "superVisorName", "address"],
        dependencies: {
          currentlyEmployed: {
            oneOf: [
              {
                properties: {
                  currentlyEmployed: { enum: [true] },
                  dateTo: { type: "string", title: "End", format: "date" },
                },
              },
              {
                properties: {
                  currentlyEmployed: { enum: [false] },
                  dateTo: { type: "string", title: "End", format: "date" },
                },
                required: ["dateTo"],
              },
            ],
          },
        },
      },
      guarantor: {
        type: "object",
        title: "Guarantor",
        properties: {
          name: { type: "string", title: "Name", minLength: 1 },
          email: {
            type: "string",
            title: "Email",
            format: "email",
            minLength: 1,
          },
        },
        required: ["name", "email"],
      },
      otherIncome: {
        type: "object",
        title: "",
        properties: {
          incomeType: {
            type: "string",
            title: "Type",
            enum: [
              "Adoption Assistance Payments",
              "Alimony",
              "Annuity",
              "Business",
              "Child Support",
              "Employment",
              "Federal Wage",
              "General Assistance/Welfare",
              "Military Pay",
              "Non-Federal Wage",
              "Other",
              "Other Income from Assets",
              "Pension",
              "Regular Gifts",
              "Section 8 Voucher",
              "Social Security",
              "SSI",
              "TANF",
              "Unemployment",
            ],
          },
          monthlyIncome: {
            type: "number",
            title: "Monthly Income",
            format: "number",
            exclusiveMinimum: 0,
          },
          effectiveDate: {
            type: "string",
            title: "Effective Date",
            format: "date",
          },
        },
        required: ["incomeType", "monthlyIncome", "effectiveDate"],
      },
    },
    type: "object",
    properties: {
      noIncome: {
        type: "boolean",
        title: t(
          "account.leaseApplication.noIncome",
          "I don't have any income"
        ),
        format: "checkbox",
        default: false,
      },
    },
  };

  const hasRentableItems = rentableTypeOptions.length > 0;

  // Generate pet type enum from petPolicy
  const allowedPetPolicies = React.useMemo(() => {
    if (!Array.isArray(unitData?.petPolicy)) return [];
    return unitData.petPolicy.filter(
      (pP: any) =>
        String(pP?.$Allowed).toLowerCase() === "true" && pP?.Pets?.$PetType
    );
  }, [unitData?.petPolicy]);

  const petTypeEnum = React.useMemo(() => {
    if (allowedPetPolicies.length === 0) {
      return ["Dog", "Cat", "Bird", "Fish", "Other"];
    }
    return allowedPetPolicies.map((pP: any) => pP.Pets.$PetType);
  }, [allowedPetPolicies]);

  const vehicleYearEnum = React.useMemo(() => {
    const maxYear = moment().year() + 1;
    const minYear = 1950;
    const years: number[] = [];
    for (let y = maxYear; y >= minYear; y -= 1) years.push(y);
    return years;
  }, []);

  const petPolicyDescription =
    allowedPetPolicies.length > 0
      ? `<strong>Pet Policy:</strong>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr>
                  <th>Pet Type</th>
                  <th>Fees</th>
                  <th>Rent</th>
                </tr>
              </thead>
              <tbody>${allowedPetPolicies
                .map(
                  (pP: any) => `<tr style="text-align: center;">
                  <td>${escapeHtml(pP.Pets?.$PetType)}</td>
                  <td>$${escapeHtml(petFeeAmount(pP.Fee))}</td>
                  <td>$${escapeHtml(petFeeAmount(pP.Rent))}</td>
                </tr>`
                )
                .join("")}</tbody>
            </table>`
      : `<strong>Pet Policy:</strong> ${escapeHtml(
          t(
            "account.leaseApplication.petPolicyUnavailable",
            "Contact the leasing office for pet policy details for this unit."
          )
        )}`;

  const maxPetsAllowed = Number.isFinite(pets) && pets > 0 ? pets : 2;

  const schema6: RJSFSchema = {
    stepTitle: t("account.leaseApplication.petsVehicles"),
    definitions: {
      pSchema: {
        type: "object",
        title: "Pet",
        properties: {
          type: {
            type: "string",
            title: "Type",
            enum: petTypeEnum,
          },
          name: { type: "string", title: "Name", pattern: "^[a-zA-Z0-9 ]+$" },
          breed: { type: "string", title: "Breed", pattern: "^[a-zA-Z0-9 ]+$" },
          color: { type: "string", title: "Color", pattern: "^[a-zA-Z0-9 ]+$" },
          age: { type: "number", title: "Age (years)" },
          weight: { type: "number", title: "Weight (lbs.)" },
        },
        required: ["name", "type", "breed", "color", "age", "weight"],
      },
      vehicle: {
        type: "object",
        title: "Vehicle",
        properties: {
          year: {
            type: "number",
            title: "Year",
            enum: vehicleYearEnum,
          },
          make: { type: "string", title: "Make" },
          model: { type: "string", title: "Model" },
          color: { type: "string", title: "Color" },
          licensePlateNumber: {
            type: "string",
            title: "License plate number",
            maxLength: 10,
            pattern: "^[A-Za-z0-9]+$",
          },
          licensePlateState: globalSchema.definitions!.state,
        },
        required: [
          "year",
          "make",
          "model",
          "color",
          "licensePlateNumber",
          "licensePlateState",
        ],
      },
      ...(hasRentableItems
        ? {
            rentableItem: {
              type: "object",
              title: t("account.leaseApplication.rentableItems"),
              properties: {
                rentableAmount: {
                  type: "string",
                  title: t(
                    "account.leaseApplication.monthlyCharge",
                    "Monthly Charge"
                  ),
                  readOnly: true,
                },
                rentableType: {
                  type: "string",
                  title: "Type",
                  oneOf: rentableTypeOptions.map((opt: any) => ({
                    const: opt.const,
                    title: opt.title,
                  })),
                },
              },
              required: ["rentableType"],
            },
          }
        : {}),
    },
    type: "object",
    title: "Anything else you'd like to add?",
    description: petPolicyDescription,
    properties: {
      pets: {
        title: t("account.leaseApplication.addPetsMax", {
          count: maxPetsAllowed,
          defaultValue: `Add Pets (Max ${maxPetsAllowed} Per Unit)`,
        }),
        type: "array",
        maxItems: maxPetsAllowed,
        items: {
          $ref: "#/definitions/pSchema",
        },
      },
      hasVehicles: {
        type: "string",
        title: "Do you have any vehicles to register?",
        enum: ["Yes", "No"],
      },
      ...(hasRentableItems
        ? {
            rentableItems: {
              title: t("account.leaseApplication.rentableItems"),
              type: "array",
              items: {
                type: "string",
              },
            },
          }
        : {}),
    },
    required: ["hasVehicles"],
    dependencies: {
      hasVehicles: {
        oneOf: [
          {
            properties: {
              hasVehicles: { enum: ["Yes"] },
              vehicles: {
                title: "Add Vehicle",
                type: "array",
                minItems: 1,
                items: {
                  $ref: "#/definitions/vehicle",
                },
              },
            },
            required: ["vehicles"],
          },
          {
            properties: {
              hasVehicles: { enum: ["No"] },
            },
          },
        ],
      },
    },
  };
  const schema12: RJSFSchema = {
    stepTitle: t("account.leaseApplication.members"),
    definitions: {
      otherApplicants: {
        type: "object",
        title: "",
        properties: {
          householdStatus: {
            type: "string",
            title: "Relationship",
            enum: [
              "Spouse/Cohabitant",
              "Family Member - Adult",
              "Family Member - Minor",
              "Roommate",
              "Live-In Aide",
            ],
          },
          name: { type: "string", title: "Name", minLength: 1 },
        },
        dependencies: {
          householdStatus: {
            oneOf: [
              {
                properties: {
                  householdStatus: {
                    enum: [
                      "Spouse/Cohabitant",
                      "Family Member - Adult",
                      "Roommate",
                      "Live-In Aide",
                    ],
                  },
                  email: { type: "string", title: "Email", format: "email" },
                },
                required: ["email"],
              },
              {
                properties: {
                  householdStatus: {
                    enum: ["Family Member - Minor"],
                  },
                  birthDate: {
                    type: "string",
                    title: "Birth Date",
                    format: "date",
                  },
                },
                required: ["birthDate"],
              },
            ],
          },
        },
        required: ["householdStatus", "name"],
      },
    },
    type: "object",
    title: "",
    properties: {
      hasOtherOccupants: {
        type: "string",
        title: "Will anyone else be living here",
        description: t(
          "account.leaseApplication.willAnyoneElseLiveHelp",
          "Include every adult and minor who will live in the unit (not including yourself)."
        ),
        enum: ["Yes", "No"],
      },
    },
    required: ["hasOtherOccupants"],
    dependencies: {
      hasOtherOccupants: {
        oneOf: [
          {
            properties: {
              hasOtherOccupants: { enum: ["Yes"] },
              otherApplicants: {
                type: "object",
                title: "",
                properties: {
                  otherApplicants: {
                    title: `Add Members(Max ${2 * beds})`,
                    type: "array",
                    minItems: 1,
                    maxItems: 2 * beds,
                    items: {
                      $ref: "#/definitions/otherApplicants",
                    },
                  },
                },
              },
            },
            required: ["otherApplicants"],
          },
          {
            properties: {
              hasOtherOccupants: { enum: ["No"] },
            },
          },
        ],
      },
    },
  };

  const schema9: RJSFSchema = {
    stepTitle: t("account.leaseApplication.termsConditions"),
    type: "object",
    title: "Terms and Conditions",
    properties: {
      termsAndConditionsContent: { type: "string", title: `` },
      agreetoTerms: {
        type: "boolean",
        title: "I agree to the terms and conditions",
        format: "checkbox",
      },
    },
    required: ["agreetoTerms"],
  };
  const schema11: RJSFSchema = {
    stepTitle: t("account.leaseApplication.review"),
    type: "object",
    title: "Review Information",
    properties: {
      reviewContent: { type: "string", title: `` },
      agreeToReview: {
        type: "boolean",
        title: "I confirm the information provided is correct to my knowledge",
        format: "checkbox",
      },
    },
    required: ["agreeToReview"],
  };

  const [formData, setFormData] = useState<any>({});
  const formRef = useRef<any>(null);
  // Guards against double-submit on the final step: postLeaseApplication is not
  // idempotent (a second click would create a second lease in ResMan).
  const isSubmittingRef = useRef(false);

  // Calculate no income help text dynamically
  const noIncomeHelpText = React.useMemo(() => {
    const incomeAmount = formData?.incomeAmount || 0;
    const incomeFrequency = formData?.incomeFrequency;

    const toMonthly = (amount: number, frequency: string) => {
      if (!amount) return 0;
      switch (frequency) {
        case "Weekly":
          return amount * 4.345;
        case "Biweekly":
          return amount * 2.1725;
        case "Annually":
          return amount / 12;
        default:
          return amount;
      }
    };

    const currentEmploymentIncome =
      formData?.currentEmployment?.monthlyIncome || 0;
    const otherIncomes = formData?.otherIncome || [];
    const totalOtherIncome = otherIncomes.reduce(
      (sum: number, income: any) => sum + (income.monthlyIncome || 0),
      0
    );

    const totalMonthlyIncome =
      toMonthly(incomeAmount, incomeFrequency) +
      currentEmploymentIncome +
      totalOtherIncome;

    const selectedUnitMonthlyRent = parseFloat(unitData?.rent) || 0;
    const hasNoIncome = formData?.noIncome === true;
    const needsGuarantor = isBelowMinimumRentIncome(
      totalMonthlyIncome,
      selectedUnitMonthlyRent
    );

    if (hasNoIncome) {
      return t("account.leaseApplication.noIncomeDescription", {
        incomeLevel: MINIMUM_INCOME_RENT_MULTIPLIER,
      });
    } else if (needsGuarantor && totalMonthlyIncome > 0) {
      return t("account.leaseApplication.noIncomeDescription1", {
        incomeLevel: MINIMUM_INCOME_RENT_MULTIPLIER,
      });
    } else {
      return t("account.leaseApplication.noIncomeDescription2", {
        incomeLevel: MINIMUM_INCOME_RENT_MULTIPLIER,
      });
    }
  }, [formData, unitData, t]);

  // Dynamic income/employment schema with guarantor option
  const getIncomeSchema = React.useMemo((): RJSFSchema => {
    const incomeAmount = formData?.incomeAmount || 0;
    const incomeFrequency = formData?.incomeFrequency;

    const toMonthly = (amount: number, frequency: string) => {
      if (!amount) return 0;
      switch (frequency) {
        case "Weekly":
          return amount * 4.345;
        case "Biweekly":
          return amount * 2.1725;
        case "Annually":
          return amount / 12;
        default:
          return amount;
      }
    };

    const currentEmploymentIncome =
      formData?.currentEmployment?.monthlyIncome || 0;
    const otherIncomes = formData?.otherIncome || [];
    const totalOtherIncome = otherIncomes.reduce(
      (sum: number, income: any) => sum + (income.monthlyIncome || 0),
      0
    );

    const totalMonthlyIncome =
      toMonthly(incomeAmount, incomeFrequency) +
      currentEmploymentIncome +
      totalOtherIncome;

    const selectedUnitMonthlyRent = parseFloat(unitData?.rent) || 0;

    const needsGuarantor = isBelowMinimumRentIncome(
      totalMonthlyIncome,
      selectedUnitMonthlyRent
    );

    const hasNoIncome = formData?.noIncome === true;
    const isGuarantor = role === "Guarantor";

    // Check if there are co-applicants (household members)
    const hasCoApplicants =
      formData?.otherApplicants?.otherApplicants &&
      formData?.otherApplicants?.otherApplicants?.length > 0 &&
      formData?.otherApplicants?.otherApplicants.some((applicant: any) =>
        ["Spouse/Cohabitant", "Family Member - Adult", "Roommate"].includes(
          applicant.householdStatus
        )
      );

    // Condition: no income or less than 2.5x rent, AND has co-applicants
    // Don't show for guarantors
    const shouldShowCoApplicantQuestion =
      !isGuarantor && (hasNoIncome || needsGuarantor) && hasCoApplicants;

    // If user wants to use co-applicant income, don't offer guarantor
    // Offer guarantor to head of household without co-applicants
    // Only show guarantor if co-applicant question is answered with "No"
    // Don't show for guarantors
    const shouldOfferGuarantor =
      !isGuarantor &&
      (hasCoApplicants
        ? formData?.useCoApplicantIncome === "No"
        : role === "Head of Household") &&
      (hasNoIncome ||
        (needsGuarantor && totalMonthlyIncome && incomeFrequency));

    // Create a deep copy of schema4 to avoid mutating the original
    const incomeSchema: RJSFSchema = JSON.parse(JSON.stringify(schema4));

    // For guarantors, strip noIncome and always require income fields
    if (isGuarantor) {
      // remove noIncome toggle if present
      if (incomeSchema.properties?.noIncome) {
        const { noIncome, ...rest } = incomeSchema.properties as any;
        incomeSchema.properties = rest;
      }

      incomeSchema.properties = {
        ...incomeSchema.properties,
        incomeAmount: {
          type: "number",
          title: t("account.leaseApplication.incomeAmount", "Income Amount"),
          format: "number",
          exclusiveMinimum: 0,
        },
        incomeFrequency: {
          type: "string",
          title: t(
            "account.leaseApplication.incomeFrequency",
            "Income Frequency"
          ),
          enum: ["Monthly", "Biweekly", "Weekly", "Annually"],
        },
      };

      // Mark as required for guarantors
      incomeSchema.required = ["incomeAmount", "incomeFrequency"];
    } else if (!hasNoIncome) {
      // For others, add income fields only if noIncome is false
      incomeSchema.properties = {
        ...incomeSchema.properties,
        incomeAmount: {
          type: "number",
          title: t("account.leaseApplication.incomeAmount", "Income Amount"),
          format: "number",
          exclusiveMinimum: 0,
        },
        incomeFrequency: {
          type: "string",
          title: t(
            "account.leaseApplication.incomeFrequency",
            "Income Frequency"
          ),
          enum: ["Monthly", "Biweekly", "Weekly", "Annually"],
        },
      };

      // Mark as required if income fields are shown
      incomeSchema.required = ["incomeAmount", "incomeFrequency"];
    }

    // Only add currentEmployment if income is provided
    if (incomeAmount && incomeFrequency && !hasNoIncome) {
      if (!incomeSchema.definitions) {
        incomeSchema.definitions = {};
      }
      incomeSchema.properties = {
        ...incomeSchema.properties,
        currentEmployment: {
          $ref: "#/definitions/employment",
        },
        otherIncome: {
          title: "Additional Income Sources",
          description: t(
            "account.leaseApplication.otherIncomeHelp",
            "Optional. Add other income (child support, benefits, gifts, etc.) that should count toward qualification."
          ),
          type: "array",
          items: {
            $ref: "#/definitions/otherIncome",
          },
        },
      };
    }

    // Add co-applicant question if needed (not for guarantors)
    if (shouldShowCoApplicantQuestion) {
      incomeSchema.properties = {
        ...incomeSchema.properties,
        useCoApplicantIncomeText: {
          type: "string",
          description: t(
            "account.leaseApplication.useCoApplicantIncomeDescription",
            "We noticed you have household members added to your application. Would you like to continue with a co-applicant's income to strengthen your application?"
          ),
        },
        useCoApplicantIncome: {
          type: "string",
          title: t(
            "account.leaseApplication.useCoApplicantIncomeTitle",
            "Use Co-applicant's Income?"
          ),
          enum: ["Yes", "No"],
          default: "",
        },
      };
      // Make it required if shown
      incomeSchema.required = incomeSchema.required
        ? [...incomeSchema.required, "useCoApplicantIncome"]
        : ["useCoApplicantIncome"];
    }

    // Add guarantors if needed (but not if using co-applicant income or role is guarantor)
    if (shouldOfferGuarantor) {
      incomeSchema.properties = {
        ...incomeSchema.properties,
        guarantors: {
          title: "Add Guarantors",
          description: t("account.leaseApplication.addGuarantorsDescription"),
          type: "array",
          maxItems: 1,
          default: [],
          items: {
            $ref: "#/definitions/guarantor",
          },
        },
      };
    }

    return incomeSchema;
  }, [formData, unitData, schema4, t, role]);

  const schemas = React.useMemo(() => {
    const baseSchemas = [];

    // Only add identity verification step if enabled for this property
    if (
      propertyDetails.isIdentityVerificationEnabled &&
      !idVerificationByPassed
    ) {
      baseSchemas.push(getTranslatedSchema(schema2));
    }

    baseSchemas.push(
      getTranslatedSchema(schema1),
      getTranslatedSchema(schema3)
    );

    if (role === "Head of Household") {
      baseSchemas.push(getTranslatedSchema(schema12));
    }

    if (role !== "Guarantor") {
      baseSchemas.push(getTranslatedSchema(schema6));
    }

    if (role !== "Live-In Aide") {
      baseSchemas.push(getTranslatedSchema(getIncomeSchema));
    }

    // Review (schema11) is the final step. Proofup income verification has been
    // moved to after fee payment (see LeasePayment) to reduce drop-off before
    // payment.
    baseSchemas.push(
      getTranslatedSchema(schema9),
      getTranslatedSchema(schema11)
    );

    return baseSchemas;
  }, [
    propertyDetails.isIdentityVerificationEnabled,
    idVerificationByPassed,
    propertyDetails.isProofupVerificationEnabled,
    personId,
    getIncomeSchema,
    getTranslatedSchema,
  ]);

  // Function to determine the first incomplete step
  const findFirstIncompleteStep = (formData: any, schemas: any[]) => {
    for (let i = 0; i < schemas.length; i++) {
      const schema = schemas[i];
      const isStepComplete = checkStepCompletion(formData, schema);
      if (!isStepComplete) {
        return i;
      }
    }
    return 0; // Return first step if all seem complete or no data
  };

  // Function to check if a step is complete based on its required fields
  const checkStepCompletion = (formData: any, schema: any) => {
    if (!formData || !schema?.properties) return false;

    // Check required fields for each property in the schema
    for (const [propKey, propSchema] of Object.entries(schema.properties)) {
      const propData = formData[propKey];

      // If property is missing from formData
      if (!propData) return false;

      // Check if property has required fields
      if ((propSchema as any)?.required) {
        const required = (propSchema as any).required;
        for (const requiredField of required) {
          if (!propData[requiredField]) return false;
        }
      }

      // For objects with definitions, check their required fields
      if ((propSchema as any)?.$ref) {
        const refPath = (propSchema as any).$ref.replace("#/definitions/", "");
        const definition = schema.definitions?.[refPath];
        if (definition?.required) {
          for (const requiredField of definition.required) {
            if (!propData[requiredField]) return false;
          }
        }
      }
    }
    if (
      schema.properties?.hasOtherOccupants &&
      formData.hasOtherOccupants === "Yes" &&
      !(formData.otherApplicants?.otherApplicants?.length > 0)
    ) {
      return false;
    }
    if (
      schema.properties?.hasVehicles &&
      formData.hasVehicles === "Yes" &&
      !(Array.isArray(formData.vehicles) && formData.vehicles.length > 0)
    ) {
      return false;
    }
    return true;
  };

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [moveInTooSoon, setMoveInTooSoon] = useState<MoveInTooSoonInfo | null>(
    null
  );
  const pendingLeaseSubmitRef = useRef<{
    payload: any;
    applicationData: any;
  } | null>(null);
  const [postLeaseApplication, { isLoading: isLoadingPostLease }] =
    usePostLeaseApplicationMutation();

  const [postApplication] = usePostApplicationMutation();

  const [getApplications] = useGetApplicationsMutation();

  const [step, setStep] = useState(0);

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Generate step titles based on current schemas
  const stepTitles = React.useMemo(() => {
    const titles: string[] = [];
    schemas.forEach((schema, index) => {
      if (schema.stepTitle) titles.push(schema.stepTitle);
      else titles.push(`${t("account.leaseApplication.step")} ${index + 1}`);
    });
    return titles;
  }, [schemas, t]);

  const [
    getPropertiesForIDMutation,
    {
      isError: isGetPropertiesError,
      isLoading: isLoadingGetPropertiesForID,
      error: getPropertiesError,
    },
  ] = useGetPropertiesMutation();

  const [saveApplicationData] = useSaveApplicationDataMutation();

  const customValidate = (formData: any, errors: any) => {
    if (formData.applicantInfo?.phone?.toString()?.length !== 10) {
      errors.applicantInfo?.phone?.addError(
        t("account.leaseApplication.phoneValidation")
      );
    }

    // Validate SSN is required if noSSN checkbox is not selected
    if (formData.applicantInfo?.identification?.noSSN !== true) {
      const ssnValue = formData.applicantInfo?.identification?.ssn;
      if (!ssnValue || ssnValue.trim() === "") {
        errors.applicantInfo?.identification?.ssn?.addError(
          "Social Security Number is required"
        );
      }
    }

    if (!formData.plaidIdentityVerification) {
      errors.plaidIdentityVerification?.addError(
        t("account.plaidIdentityVerification.title")
      );
    }

    if (!formData.incomeVerification) {
      errors.incomeVerification?.addError(
        t("account.incomeVerification.requiredError")
      );
    }

    if (formData.agreetoTerms !== true) {
      errors.agreetoTerms?.addError(
        t("account.leaseApplication.agreeValidation")
      );
    }

    if (formData.agreeToReview !== true) {
      errors.agreeToReview?.addError(
        t("account.leaseApplication.agreeValidation")
      );
    }

    if (formData.currentAddress?.moveInDate) {
      const moveIn = moment(formData.currentAddress.moveInDate, "YYYY-MM-DD");
      const min = moment(moveInMinDate, "YYYY-MM-DD");
      const max = moment(moveInMaxDate, "YYYY-MM-DD");
      if (!moveIn.isValid() || moveIn.isBefore(min) || moveIn.isAfter(max)) {
        errors.currentAddress?.moveInDate?.addError(
          t(
            "account.leaseApplication.moveInDateRange",
            "Move-in date must be within the last 60 years."
          )
        );
      }
    }

    // Check for duplicate emails across applicant, other applicants, and guarantors
    const applicantEmail = formData.applicantInfo?.email?.toLowerCase().trim();
    const emailSet = new Set<string>();

    if (applicantEmail) {
      emailSet.add(applicantEmail);
    }

    // Check other applicants for duplicate emails
    if (formData.otherApplicants?.otherApplicants?.length > 0) {
      formData.otherApplicants.otherApplicants.forEach(
        (applicant: any, index: number) => {
          // Require a non-empty full name (single "Name" field, 2+ words).
          // RJSF required alone allows name: "" — that used to reach ResMan as
          // Customer.Name undefined.
          const nameParts = (applicant.name ?? "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          if (nameParts.length === 0) {
            errors.otherApplicants.otherApplicants[index].name?.addError(
              t("account.leaseApplication.nameRequired", "Name is required")
            );
          } else if (nameParts.length < 2) {
            errors.otherApplicants.otherApplicants[index].name?.addError(
              t("account.leaseApplication.fullNameValidation")
            );
          }

          // Validate dependent age
          if (
            applicant.birthDate &&
            applicant.householdStatus === "Family Member - Minor"
          ) {
            const ageInDays = moment().diff(
              moment(applicant.birthDate, "YYYY-MM-DD"),
              "days"
            );
            if (ageInDays < 0 || ageInDays > 365 * 18) {
              errors.otherApplicants.otherApplicants[index].birthDate?.addError(
                t("account.leaseApplication.dependentAgeValidation")
              );
            }
          }

          // Check for duplicate email (Spouse and Roommate have email field)
          if (applicant.email) {
            const otherApplicantEmail = applicant.email.toLowerCase().trim();
            if (emailSet.has(otherApplicantEmail)) {
              errors.otherApplicants.otherApplicants[index].email?.addError(
                "This email is already used by another applicant. Please use a unique email address."
              );
            } else {
              emailSet.add(otherApplicantEmail);
            }
          }
        }
      );
    }

    // Check guarantors for required name/email and duplicate emails
    if (formData.guarantors?.length > 0) {
      if (!errors.guarantors) {
        errors.guarantors = [];
      }

      formData.guarantors.forEach((guarantor: any, index: number) => {
        if (!errors.guarantors[index]) {
          errors.guarantors[index] = {};
        }

        const nameParts = (guarantor?.name ?? "")
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        if (nameParts.length === 0) {
          if (!errors.guarantors[index].name) {
            errors.guarantors[index].name = {
              __errors: [],
              addError(msg: string) {
                this.__errors.push(msg);
              },
            };
          }
          errors.guarantors[index].name?.addError?.(
            t("account.leaseApplication.nameRequired", "Name is required")
          );
        } else if (nameParts.length < 2) {
          if (!errors.guarantors[index].name) {
            errors.guarantors[index].name = {
              __errors: [],
              addError(msg: string) {
                this.__errors.push(msg);
              },
            };
          }
          errors.guarantors[index].name?.addError?.(
            t("account.leaseApplication.fullNameValidation")
          );
        }

        if (!guarantor?.email?.trim()) {
          if (!errors.guarantors[index].email) {
            errors.guarantors[index].email = {
              __errors: [],
              addError(msg: string) {
                this.__errors.push(msg);
              },
            };
          }
          errors.guarantors[index].email?.addError?.(
            t("account.leaseApplication.emailRequired", "Email is required")
          );
        } else {
          const guarantorEmail = guarantor.email.toLowerCase().trim();
          if (emailSet.has(guarantorEmail)) {
            if (!errors.guarantors[index].email) {
              errors.guarantors[index].email = {};
            }
            if (!errors.guarantors[index].email.addError) {
              errors.guarantors[index].email = {
                __errors: [],
                addError(msg: string) {
                  this.__errors.push(msg);
                },
              };
            }
            errors.guarantors[index].email?.addError?.(
              "This email is already used by another applicant or guarantor. Please use a unique email address."
            );
          } else {
            emailSet.add(guarantorEmail);
          }
        }
      });
    }

    return errors;
  };
  // @ts-ignore
  const handleNext = (nextData) => {
    console.log(stepTitles[step]);
    // Drop RJSF placeholder roster rows (`{}`) so they never persist / finalize.
    const merged = { ...nextData, ...formData };
    if (Array.isArray(merged.guarantors)) {
      merged.guarantors = merged.guarantors.filter((g: any) =>
        Boolean(
          g && Object.keys(g).some((k) => String(g[k] ?? "").trim().length > 0)
        )
      );
    }
    if (merged.hasVehicles === "No") {
      merged.vehicles = [];
    }
    if (merged.hasOtherOccupants === "No") {
      merged.otherApplicants = {
        ...(merged.otherApplicants ?? {}),
        otherApplicants: [],
      };
    }
    if (Array.isArray(merged.otherApplicants?.otherApplicants)) {
      merged.otherApplicants = {
        ...merged.otherApplicants,
        otherApplicants: merged.otherApplicants.otherApplicants.filter(
          (a: any) =>
            Boolean(
              a &&
                Object.keys(a).some((k) => String(a[k] ?? "").trim().length > 0)
            )
        ),
      };
    }
    const newFormData = merged;
    setFormData(newFormData);
    if (applicationId) {
      const isReviewStep =
        stepTitles[step] === t("account.leaseApplication.review");
      saveApplicationData({
        id: applicationId,
        body: isReviewStep
          ? { applicationData: newFormData, status: "submitted" }
          : { applicationData: newFormData },
      }).then((res) => {
        // Refresh applications list in the background
        const { data } = res as { data: any };
        if (
          data &&
          formData.images &&
          formData.images.face &&
          formData.images.face.indexOf("response-content-disposition") >= 0
        ) {
          setFormData(data.applicationData);
        }
        setCompletedSteps((prev) => new Set(prev).add(step));
        setStep((prevStep) => prevStep + 1);
      });
    } else {
      setCompletedSteps((prev) => new Set(prev).add(step));
      setStep((prevStep) => prevStep + 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow navigation to completed steps or current step
    if (stepIndex <= step || completedSteps.has(stepIndex)) {
      setStep(stepIndex);
    }
  };

  const handlePrev = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const { data: userData, isLoading: isLoadingGetUser }: any =
    useGetUserQuery(null);
  const applicationUnit = unitNumber || unitID;
  const handoffLease = getCurrentLease(userData);
  const alreadyResidentOnUnit =
    isActiveLeaseStatus(handoffLease?.Status) &&
    Boolean(applicationUnit) &&
    String(handoffLease?.Unit ?? "")
      .trim()
      .toLowerCase() === String(applicationUnit).trim().toLowerCase();
  const { data: rebindLeases } = useGetRebindLeasesQuery(undefined, {
    skip: !leaseSwitcherOn || !isGetUnitError || alreadyResidentOnUnit,
  });
  const residentOffer = singleHomeRebindOffer(rebindLeases?.leases);
  const [residentHandoffOpen, setResidentHandoffOpen] = useState(false);
  const [residentHandoffError, setResidentHandoffError] = useState("");
  const [rebindToResident, { isLoading: isRebindingToResident }] =
    useRebindLeaseMutation();

  useEffect(() => {
    if (!isGetUnitError || !alreadyResidentOnUnit) return;
    clearAuthReturnTo();
    navigate("/home", { replace: true });
  }, [alreadyResidentOnUnit, isGetUnitError, navigate]);

  useEffect(() => {
    if (propertyID && !propertyDetails.name) {
      getPropertiesForIDMutation(propertyID).then((res) => {
        const { data } = res as { data: any };
        setPropertyDetails(data);
        const missingLeaseTermPath = buildMissingLeaseTermAvailabilityPath({
          property: data,
          unitID,
          unitNumber,
          moveInDate,
          leaseTerm: term,
          applicationId,
          personId,
          leaseId,
        });
        if (missingLeaseTermPath) {
          navigate(missingLeaseTermPath, { replace: true });
        }
      });
    }
    if (
      (unitID || unitNumber) &&
      moveInDate &&
      propertyID &&
      !unitData &&
      (hasLeaseTermParam(term) || Boolean(leaseId))
    ) {
      getUnitDataMutation({
        unitID: unitID ?? unitNumber,
        moveInDate,
        term,
        propertyID,
        leaseId,
      }).then((res: any) => {
        if (res?.data) {
          setUnitData(res.data);
        }
      });
    }
    if (userData?.user && formData?.applicantInfo?.email === undefined) {
      const { email, person } = userData?.user;
      setFormData({
        applicantInfo: {
          email: email,
          name: `${person.FirstName} ${person.LastName}`,
        },
      });
    }

    if (unitData?.property) {
      setPropertyDetails(unitData.property);
    }
    if (unitData?.bed) {
      setBeds(parseInt(unitData.bed));
    }
    if (unitData?.numPetsAllowed) {
      const parsedPets = parseInt(unitData.numPetsAllowed, 10);
      setPets(Number.isFinite(parsedPets) && parsedPets > 0 ? parsedPets : 2);
    }

    if (
      userData?.user &&
      (applicationId || unitData?.number) &&
      !formData.applicantInfo?.birthdate
    ) {
      getApplications({
        ...(applicationId
          ? { id: applicationId }
          : personId
          ? { personId }
          : { userId: userData?.user?.id }),
        ...(propertyID ? { propertyId: propertyID } : {}),
      }).then((res: any) => {
        setApplicationsData(res.data);
      });
    }
  }, [
    propertyID,
    userData,
    unitData,
    applicationId,
    unitNumber,
    unitID,
    moveInDate,
    term,
  ]);

  const setApplicationsData = (applicationsListData: any[]) => {
    const existingApplication = applicationId
      ? applicationsListData.find((app: any) => app.id === applicationId)
      : applicationsListData.find(
          (app: any) =>
            app.unit === unitData?.number && app.status === "pending"
        );
    if (existingApplication) {
      setPropertyID(existingApplication.property.id);
      setUnitNumber(existingApplication.unit);
      setTerm(existingApplication.term);
      setMoveInDate(existingApplication.moveInDate);
      setApplicationId(existingApplication.id);
      setIdVerificationByPassed(
        existingApplication.bypassIDVerification ?? false
      );
      if (existingApplication.lease) {
        setLeaseId(existingApplication.lease.id);
        if (
          existingApplication.lease.newPets &&
          existingApplication.lease.newPets.length > 0
        ) {
          const leasePets = existingApplication.lease.newPets;
          const uncommonPets: any[] = [];
          if (existingApplication.applicationData?.pets?.length) {
            const applicationPets = existingApplication.applicationData.pets;

            leasePets.forEach((leasePet: any) => {
              const match = applicationPets.find(
                (appPet: any) =>
                  appPet.name === leasePet.name &&
                  appPet.type === leasePet.type &&
                  appPet.breed === leasePet.breed
              );
              if (!match) {
                uncommonPets.push(leasePet);
              }
            });
            setPets(2 - uncommonPets.length);
          } else {
            setPets(2 - leasePets.length);
          }
        }
      }
      if (!personId) {
        setPersonId(existingApplication.person.id);
      }
      setRole(existingApplication.role ?? "Head of Household");
      if (existingApplication.applicationData) {
        const applicationData = {
          ...existingApplication.applicationData,
        };
        if (
          applicationData.hasVehicles !== "Yes" &&
          applicationData.hasVehicles !== "No" &&
          applicationData.vehicles?.length
        ) {
          applicationData.hasVehicles = "Yes";
        }
        if (
          applicationData.hasOtherOccupants !== "Yes" &&
          applicationData.hasOtherOccupants !== "No" &&
          applicationData.otherApplicants?.otherApplicants?.length
        ) {
          applicationData.hasOtherOccupants = "Yes";
        }
        setFormData(applicationData);
        // Navigate to the first incomplete step
        const firstIncompleteStep = findFirstIncompleteStep(
          applicationData,
          schemas
        );
        setStep(firstIncompleteStep);

        // Mark previous steps as completed
        const completedStepsSet = new Set<number>();
        for (let i = 0; i < firstIncompleteStep; i++) {
          completedStepsSet.add(i);
        }
        setCompletedSteps(completedStepsSet);
      }
    } else {
      if (!leaseId) {
        console.log(
          `Creating application for unit ${unitData?.number} and user ${userData?.user?.id}`
        );
        const { email, person } = userData?.user;
        const body = {
          unit: unitData?.number,
          moveInDate: unitData?.moveInDate,
          term: unitData?.term ?? term,
          person: { id: userData?.user?.person?.id },
          userId: userData?.user?.id,
          status: "pending",
          rent: unitData?.rent,
          property: { id: propertyID },
          applicationData: {
            applicantInfo: {
              email: email,
              name: `${person.FirstName} ${person.LastName}`,
            },
          },
          role: "Head of Household",
        };
        postApplication(body).then((res: any) => {
          if (res?.data) {
            setApplicationId(res.data.id);
          }
        });
      }
    }
  };

  function transformErrors(errors: any[]) {
    return errors.map((error) => {
      console.log(JSON.stringify(error));

      // Translate common validation error messages
      if (error.name === "required") {
        error.message = `${error.params.missingProperty} ${t(
          "account.leaseApplication.required"
        )}`;
      } else if (error.name === "minLength") {
        error.message = `${t("account.leaseApplication.minLength")} ${
          error.params?.limit
        } characters`;
      } else if (error.name === "maxLength") {
        error.message = `${t("account.leaseApplication.maxLength")} ${
          error.params?.limit
        } characters`;
      } else if (error.name === "minimum") {
        error.message = `${t("account.leaseApplication.minimum")} ${
          error.params?.limit
        }`;
      } else if (error.name === "maximum") {
        error.message = `${t("account.leaseApplication.maximum")} ${
          error.params?.limit
        }`;
      } else if (error.name === "pattern") {
        // Check if this is a pet name field
        if (
          error.schemaPath &&
          error.schemaPath.includes("pets") &&
          error.schemaPath.includes("name")
        ) {
          error.message = t("account.leaseApplication.petPattern");
        } else if (
          error.schemaPath &&
          error.schemaPath.includes("pets") &&
          (error.schemaPath.includes("color") ||
            error.schemaPath.includes("breed"))
        ) {
          error.message = t("account.leaseApplication.petPattern");
        } else if (error.schemaPath && error.schemaPath.includes("ssn")) {
          error.message = `SSN ${t("account.leaseApplication.pattern")}`;
        } else if (
          error.schemaPath &&
          error.schemaPath.includes("licensePlateNumber")
        ) {
          error.message = t("account.leaseApplication.licensePlatePattern");
        } else {
          error.message = t("account.leaseApplication.pattern");
        }
      } else if (error.name === "format") {
        if (error.params?.format === "email") {
          error.message = t("account.leaseApplication.invalidEmail");
        } else if (error.params?.format === "date") {
          error.message = t("account.leaseApplication.invalidDate");
        } else {
          error.message = `${t("account.leaseApplication.format")} "${
            error.params?.format
          }"`;
        }
      } else if (error.name === "enum") {
        error.message = t("account.leaseApplication.enum");
      } else if (error.name === "type") {
        if (
          error.params?.type === "number" ||
          error.params?.type === "integer"
        ) {
          error.message = t("account.leaseApplication.invalidNumber");
        } else {
          error.message = `should be ${error.params?.type}`;
        }
      } else if (error.message === "must match exactly one schema in oneOf") {
        error.message = "";
      } else if (
        error.message === "must be equal to one of the allowed values"
      ) {
        error.message = t("account.leaseApplication.enum");
      }

      return error;
    });
  }
  return (
    <>
      {(isLoadingGetPropertiesForID ||
        isLoadingGetUnit ||
        isLoadingGetUser ||
        isLoadingPostLease) && <ManualLoader />}
      <WizardShell
        stepper={
          <StepNavigation
            currentStep={step}
            totalSteps={schemas.length}
            onStepClick={handleStepClick}
            stepTitles={stepTitles}
            completedSteps={completedSteps}
          />
        }
      >
        <Box
          component={"div"}
          className={"container_content"}
          // ref={elementRef}
          sx={{
            [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
              minHeight: 400 + commonSpacing.desktopPaddingB * 4,
            },
          }}
        >
          <RegisterReviewContainer>
            <Box>
              {isGetUnitError || isGetPropertiesError ? (
                <div>
                  There was an error {(getUnitError as any)?.data?.message}{" "}
                  {(getPropertiesError as any)?.data?.message}
                  {isGetUnitError && residentOffer && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setResidentHandoffError("");
                          setResidentHandoffOpen(true);
                        }}
                      >
                        {t(
                          residentOffer?.propertyName
                            ? "home.rebind.residentOffer"
                            : "home.rebind.residentOfferUnit",
                          residentOffer?.propertyName
                            ? "Switch to unit {{unit}} at {{property}}"
                            : "Switch to unit {{unit}}",
                          {
                            unit: residentOffer?.unit,
                            property: residentOffer?.propertyName,
                          }
                        )}
                      </Button>
                    </Box>
                  )}
                  {isGetUnitError &&
                    !residentOffer &&
                    (rebindLeases?.leases ?? []).some(
                      (lease) => !lease.current
                    ) && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="text"
                          onClick={() => navigate("/settings/leases")}
                        >
                          {t("home.rebind.chooseLease", "Choose a lease")}
                        </Button>
                      </Box>
                    )}
                  <Dialog
                    open={residentHandoffOpen}
                    onClose={
                      isRebindingToResident
                        ? undefined
                        : () => setResidentHandoffOpen(false)
                    }
                  >
                    <DialogTitle>
                      {t("home.rebind.title", "Switch lease")}
                    </DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {t(
                          "home.rebind.confirmConnect",
                          "Switch your account to {{to}} ({{toStatus}})? You can switch back. Payment methods stay on the lease where you saved them.",
                          {
                            to: residentOffer?.unit
                              ? `unit ${residentOffer.unit}${
                                  residentOffer.propertyName
                                    ? ` at ${residentOffer.propertyName}`
                                    : ""
                                }`
                              : "this lease",
                            toStatus: residentOffer?.status,
                          }
                        )}
                      </Typography>
                      {residentHandoffError && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          {residentHandoffError}
                        </Typography>
                      )}
                    </DialogContent>
                    <DialogActions>
                      <Button
                        variant="text"
                        disabled={isRebindingToResident}
                        onClick={() => setResidentHandoffOpen(false)}
                      >
                        {t("home.rebind.cancel", "Cancel")}
                      </Button>
                      <Button
                        variant="contained"
                        disabled={isRebindingToResident || !residentOffer}
                        onClick={() => {
                          if (!residentOffer) return;
                          setResidentHandoffError("");
                          rebindToResident({
                            targetPersonId: residentOffer.personId,
                          })
                            .unwrap()
                            .then(() => navigate("/home", { replace: true }))
                            .catch(() =>
                              setResidentHandoffError(
                                t(
                                  "home.rebind.error",
                                  "That lease could not be switched. Try again."
                                )
                              )
                            );
                        }}
                      >
                        {t("home.rebind.switch", "Switch")}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <FormInputWrapper
                    sx={{
                      "& .MuiFormControl-root": {
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        "&:has(legend)": {
                          flexDirection: "column",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        order: -1,
                        marginBottom: "8px",
                      },
                    }}
                  >
                    <Form
                      ref={formRef}
                      showErrorList={false}
                      schema={schemas[step]}
                      onSubmit={async (data) => {
                        if (step === schemas.length - 1) {
                          // Final step (Review): create the lease, mark the
                          // application submitted, then send the applicant to
                          // payment. Proofup income verification now happens
                          // after payment on the LeasePayment screen.

                          // Re-entry guard: postLeaseApplication is not
                          // idempotent, so swallow rapid double-submits.
                          if (isSubmittingRef.current) {
                            return;
                          }
                          isSubmittingRef.current = true;

                          const applicationData = {
                            ...data.formData,
                            ...formData,
                          };
                          setFormData(applicationData);

                          // Pay-first (ResMan parity): do NOT create the lease
                          // here. Mark the application 'submitted' (form complete,
                          // payment pending) — the distinguisher from 'completed',
                          // which means paid + lease created at finalize — and send
                          // the applicant to payment. The lease is created
                          // server-side only after the fee is paid, and finalize
                          // then sets status='completed'. (Server-side, the PDF
                          // upload only fires once a lease exists, so this
                          // pre-lease 'submitted' is safe.)
                          // Pay-first is only for new applications before a lease
                          // exists. Co-applicants already have a leaseId, so they
                          // must fall through to the existing-lease postApplication
                          // path below.
                          if (unitData?.payFirst && !leaseId) {
                            if (applicationId) {
                              await saveApplicationData({
                                id: applicationId,
                                body: { applicationData, status: "submitted" },
                              });
                            }
                            setErrorMessage("");
                            isSubmittingRef.current = false;
                            navigate(
                              `/application/payment?applicationId=${applicationId}`
                            );
                            return;
                          }

                          const leasePayload = {
                            propertyId: propertyID,
                            unitId: unitData?.id,
                            leaseStart: unitData?.moveInDate,
                            term: unitData?.term ?? term,
                            application: applicationData,
                            leaseId: leaseId,
                            personId: personId ?? userData?.user?.person?.id,
                            applicationId: applicationId,
                          };
                          pendingLeaseSubmitRef.current = {
                            payload: leasePayload,
                            applicationData,
                          };
                          const leaseRes: any = await postLeaseApplication(
                            leasePayload
                          );
                          if (leaseRes?.error) {
                            const tooSoon = parseMoveInTooSoonError(
                              leaseRes.error
                            );
                            if (tooSoon) {
                              setMoveInTooSoon(tooSoon);
                              isSubmittingRef.current = false;
                              return;
                            }
                            setErrorMessage(
                              leaseRes?.error?.data?.message ??
                                "Unable to submit lease Application."
                            );
                            isSubmittingRef.current = false;
                            return;
                          }
                          const newLeaseId = leaseRes.data?.leaseId ?? leaseId;
                          if (!newLeaseId) {
                            // Lease wasn't created — don't navigate to a payment
                            // page with leaseid=undefined.
                            setErrorMessage(
                              "Unable to submit lease Application."
                            );
                            isSubmittingRef.current = false;
                            return;
                          }
                          setLeaseId(newLeaseId);

                          if (applicationId) {
                            await saveApplicationData({
                              id: applicationId,
                              body: {
                                applicationData,
                                status: "submitted",
                              },
                            });
                          }
                          setErrorMessage("");
                          navigate(
                            `/application/payment?leaseid=${newLeaseId}&applicationId=${applicationId}`
                          );
                        } else {
                          handleNext(data.formData);
                        }
                      }}
                      formData={formData}
                      formContext={{
                        updateVerificationData: (verificationData: any) => {
                          // Extract 10-digit phone number from E.164 format (e.g., +15129196138 -> 5129196138)
                          const phoneDigits =
                            verificationData.verifiedPhoneNumber
                              ?.replace(/\D/g, "")
                              .slice(-10);

                          setFormData((prev: any) => ({
                            ...prev,
                            applicantInfo: {
                              ...prev.applicantInfo,
                              phone: phoneDigits,
                              birthdate:
                                verificationData.verifiedDateOfBirth ||
                                prev.applicantInfo?.birthdate,
                              identification: {
                                ...prev.applicantInfo?.identification,
                                number: verificationData.document_id_number,
                                type: verificationData.document_category,
                                issuingCountry:
                                  verificationData.document_issuing_country,
                                issuingState:
                                  verificationData.document_issuing_state,
                              },
                            },
                            currentAddress: {
                              ...prev.currentAddress,
                              address: verificationData.verifiedAddress,
                            },
                            plaidIdentityVerification: "completed",
                            images: verificationData.images,
                          }));

                          setTimeout(() => {
                            formRef.current?.submit();
                          }, 3000);
                        },
                      }}
                      onChange={(e) => {
                        const newFormData = { ...e.formData };
                        if (newFormData.hasVehicles === "No") {
                          newFormData.vehicles = [];
                        }
                        if (newFormData.hasOtherOccupants === "No") {
                          newFormData.otherApplicants = {
                            ...(newFormData.otherApplicants ?? {}),
                            otherApplicants: [],
                          };
                        }

                        // Clear SSN field when "I don't have SSN" is checked
                        if (
                          newFormData?.applicantInfo?.identification?.noSSN ===
                          true
                        ) {
                          if (
                            newFormData.applicantInfo?.identification?.ssn !==
                            undefined
                          ) {
                            newFormData.applicantInfo.identification.ssn =
                              undefined;
                          }
                        }

                        // Current employment: hide End date and clear it
                        if (
                          newFormData?.currentEmployment?.currentlyEmployed ===
                          true
                        ) {
                          newFormData.currentEmployment = {
                            ...newFormData.currentEmployment,
                            dateTo: undefined,
                          };
                        }

                        setFormData(newFormData);
                      }}
                      validator={validator}
                      customValidate={customValidate}
                      noHtml5Validate={true}
                      transformErrors={transformErrors}
                      focusOnFirstError={true}
                      widgets={{
                        FileWidget: CustomFileWidget,
                        CheckboxWidget: CustomCheckboxWidget,
                        PhoneWidget: PhoneWidget,
                        SSNWidget: SSNWidget,
                        RentableItemsWidget: RentableItemsWidget,
                      }}
                      templates={{
                        ObjectFieldTemplate: CustomObjectFieldTemplate,
                        ArrayFieldTemplate: CustomArrayFieldTemplate,
                      }}
                      fields={{
                        readOnlyField: ReadOnlyFieldTemplate,
                      }}
                      uiSchema={{
                        applicantInfo: {
                          name: {
                            "ui:readonly":
                              propertyDetails.isIdentityVerificationEnabled &&
                              !idVerificationByPassed,
                          },
                          birthdate: {
                            "ui:readonly":
                              propertyDetails.isIdentityVerificationEnabled &&
                              !idVerificationByPassed,
                          },
                          phone: {
                            "ui:widget": "PhoneWidget",
                            "ui:readonly":
                              propertyDetails.isIdentityVerificationEnabled &&
                              !idVerificationByPassed,
                          },
                          identification: {
                            number: {
                              "ui:readonly":
                                propertyDetails.isIdentityVerificationEnabled &&
                                !idVerificationByPassed,
                            },
                            type: {
                              "ui:readonly":
                                propertyDetails.isIdentityVerificationEnabled &&
                                !idVerificationByPassed,
                            },
                            ssn: {
                              "ui:widget": formData?.applicantInfo
                                ?.identification?.noSSN
                                ? "hidden"
                                : "SSNWidget",
                            },
                            noSSN: {
                              "ui:widget": "CheckboxWidget",
                            },
                            usCitizen: {
                              "ui:widget": formData?.applicantInfo
                                ?.identification?.noSSN
                                ? "hidden"
                                : "CheckboxWidget",
                            },
                          },
                          emergencyContact: {
                            phone: {
                              "ui:widget": "PhoneWidget",
                            },
                          },
                        },
                        currentAddress: {
                          rented: {
                            "ui:widget": "CheckboxWidget",
                          },
                          landlordPhone: {
                            "ui:widget": "PhoneWidget",
                          },
                        },
                        hasVehicles: {
                          "ui:widget": "radio",
                        },
                        hasOtherOccupants: {
                          "ui:widget": "radio",
                        },
                        noIncome: {
                          "ui:widget":
                            role === "Guarantor" ? "hidden" : "CheckboxWidget",
                          "ui:help": noIncomeHelpText,
                        },
                        currentEmployment: {
                          currentlyEmployed: {
                            "ui:widget": "CheckboxWidget",
                          },
                          dateTo: {
                            "ui:widget": formData?.currentEmployment
                              ?.currentlyEmployed
                              ? "hidden"
                              : undefined,
                          },
                          superVisorPhone: {
                            "ui:widget": "PhoneWidget",
                          },
                        },
                        guarantors: {
                          "ui:help": t(
                            "account.leaseApplication.addGuarantorsDescription",
                            "Do you want to add any guarantors to support your application?"
                          ),
                          items: {
                            phone: {
                              "ui:widget": "PhoneWidget",
                            },
                          },
                        },
                        useCoApplicantIncome: {
                          "ui:help": "",
                          "ui:options": {
                            inline: true,
                          },
                        },
                        useCoApplicantIncomeText: {
                          "ui:field": "readOnlyField",
                        },
                        agreetoTerms: {
                          "ui:widget": "CheckboxWidget",
                        },
                        agreeToReview: {
                          "ui:widget": "CheckboxWidget",
                        },
                        plaidIdentityVerification: {
                          "ui:widget": PlaidIdentityVerificationWidget,
                          "ui:options": {
                            personId: personId ?? userData?.user?.person?.id,
                            formData: formData,
                          },
                        },
                        incomeVerification: {
                          "ui:widget": IncomeVerificationWidget,
                          "ui:options": {
                            personId: personId ?? userData?.user?.person?.id,
                            leaseId: leaseId,
                            propertyId: propertyID,
                            formData: formData,
                          },
                        },
                        termsAndConditionsContent: {
                          "ui:widget": TermsAndConditionsWidget,
                        },
                        reviewContent: {
                          "ui:widget": ReviewInformation,
                          "ui:options": {
                            schemas: schemas,
                            formData: formData,
                            adminFee: unitData?.adminFee,
                            applicationFee: unitData?.applicationFee,
                            leaseId: leaseId,
                            role: role,
                            deposit: unitData?.deposit,
                          },
                        },
                        ...(hasRentableItems
                          ? {
                              rentableItems: {
                                "ui:widget": "RentableItemsWidget",
                                "ui:options": {
                                  rentableTypeOptions: rentableTypeOptions,
                                },
                              },
                            }
                          : {}),
                      }}
                    >
                      {moveInTooSoon && applicationId && (
                        <MoveInDateCorrection
                          applicationId={applicationId}
                          earliestMoveInDate={moveInTooSoon.earliestMoveInDate}
                          message={moveInTooSoon.message}
                          onSaved={async (isoDate) => {
                            setMoveInDate(isoDate);
                            setUnitData((prev: any) =>
                              prev ? { ...prev, moveInDate: isoDate } : prev
                            );
                            setMoveInTooSoon(null);
                            const pending = pendingLeaseSubmitRef.current;
                            if (!pending) return;
                            isSubmittingRef.current = true;
                            const leaseRes: any = await postLeaseApplication({
                              ...pending.payload,
                              leaseStart: isoDate,
                            });
                            if (leaseRes?.error) {
                              const tooSoon = parseMoveInTooSoonError(
                                leaseRes.error
                              );
                              if (tooSoon) {
                                setMoveInTooSoon(tooSoon);
                                isSubmittingRef.current = false;
                                return;
                              }
                              setErrorMessage(
                                leaseRes?.error?.data?.message ??
                                  "Unable to submit lease Application."
                              );
                              isSubmittingRef.current = false;
                              return;
                            }
                            const newLeaseId =
                              leaseRes.data?.leaseId ?? leaseId;
                            if (!newLeaseId) {
                              setErrorMessage(
                                "Unable to submit lease Application."
                              );
                              isSubmittingRef.current = false;
                              return;
                            }
                            setLeaseId(newLeaseId);
                            if (applicationId) {
                              await saveApplicationData({
                                id: applicationId,
                                body: {
                                  applicationData: pending.applicationData,
                                  status: "submitted",
                                },
                              });
                            }
                            isSubmittingRef.current = false;
                            navigate(
                              `/application/payment?leaseid=${newLeaseId}&applicationId=${applicationId}`
                            );
                          }}
                        />
                      )}
                      {errorMessage !== "" && !moveInTooSoon && (
                        <Messages
                          messageHeading={"Lease Application"}
                          messageContent={errorMessage}
                          closeEvent={() => setErrorMessage("")}
                          bottomMargin={4}
                          topMargin={4}
                          hideCloseIcon={true}
                        />
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "2px",
                        }}
                      >
                        {step > 0 && (
                          <Button
                            variant="contained"
                            style={{ display: "flex" }}
                            color="primary"
                            onClick={handlePrev}
                          >
                            {t("account.leaseApplication.previous")}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          style={{ display: "flex" }}
                          color="primary"
                          type={"submit"}
                          disabled={
                            errorMessage !== "" ||
                            !!moveInTooSoon ||
                            isLoadingPostLease
                          }
                        >
                          {step === schemas.length - 1
                            ? t("account.leaseApplication.submit")
                            : t("account.leaseApplication.next")}
                        </Button>
                      </div>
                    </Form>
                  </FormInputWrapper>
                </div>
              )}
            </Box>
          </RegisterReviewContainer>
        </Box>
        <Box>
          <Footer />
        </Box>
      </WizardShell>
    </>
  );
};
